import { getAssetPath } from '@/constants/configs';

import { QuestionCode } from '@/types/api/pep';
import {
  AppConfigurations,
  ExtraBasicInfoQuestions,
} from '@/types/app-configurations';
import { Option } from '@/types/components/common';
import { DocumentType } from '@/types/document';
import { CountryISO } from '@/types/places';
import { User } from '@/types/user';

import { sortStringArray } from '@/utils/string';
import {
  hasNonUSIssuedPassport,
  isExpectedMonthlyDepositCollected,
} from '@/utils/user';

import { defaultConfigs, resolveICBWebUrl } from './default';

export const caymanConfigs: AppConfigurations = {
  ...defaultConfigs,
  links: {
    ...defaultConfigs.links,
    privacyPolicyURL: 'https://www.caymannational.com/about/privacy-notice',
    termsAndConditionsURL: getAssetPath('/tnc/republiconboard-tnc-cym.pdf'),
    termsOfUseURL: getAssetPath('/tnc/republiconboard-tnc-cym.pdf'),
    appTermsAndConditionsURL: getAssetPath('/tnc/republiconboard-tnc-cym.pdf'),
    accountOpeningRequirementUrl: getAssetPath(
      '/account-opening-requirements/account-opening-requirements-gdo-cayman.pdf',
    ),
    biometricConsentURL: getAssetPath(
      '/bio-consent/republiconboard-biometric-consent-cym.pdf',
    ),
    icbWeb: resolveICBWebUrl('https://online.caymannational.com/#/login'),
    icbIOS: 'https://online.caymannational.com/#/login',
    icbAndroid: 'https://online.caymannational.com/#/login',
  },
  logo: getAssetPath('/cayman-logo-with-text.svg'),
  brandingLogoLight: getAssetPath('/cayman-logo-white-text.svg'),
  brandingImageInfoPage: getAssetPath('/bg-cayman-male-cropped.png'),
  brandingImageAddressPage: getAssetPath('/bg-cayman-female-cropped.png'),
  headerLogoIconOnDark: 'logo-cayman-light',
  headerLogoIconOnLight: 'logo-cayman',
  headerLogoIconSx: { width: 165, height: 44 },
  defaultCountryCode: '1345',
  locale: 'en-KY',
  isoCode: 'CYM',
  dateFormat: 'dd/MM/yyyy',
  defaultCurrency: 'KYD',
  showSelfEmployedStartupQuestion: false,
  defaultIdentityType: DocumentType.Passport,
  idCaptureRequirement: 'passport',
  idDocumentTypes: [DocumentType.Passport],
  showRiskSectorFlagQuestion: false,
  shouldShowGDPRDrawer: true,
  showSelfEmployedWithRiskSectorFlagQuestion: false,
  shouldShowEmailNotificationPopup: true,
  extraPhoneOptions: [
    { value: '1-US', text: '+1', icon: 'flag-united-states' },
    { value: '1-Canada', text: '+1', icon: 'flag-canada' },
    { value: '44-UK', text: '+44', icon: 'flag-united-kingdom' },
    { value: '63-Philippines', text: '+63', icon: 'flag-philippines' },
    { value: '1-Jamaica', text: '+1', icon: 'flag-jamaica' },
    { value: 'Other', text: 'Others' },
  ],
  maxTaxResidencySelection: 6,
  isExcEnabled: false,
  isBranchSelectionEnabled: true,
  shouldShowUsername: false,
  shouldShowIssuance: true,
  shouldShowCityIssuance: true,
  shouldShowCityOfBirth: true,
  shouldShowLeftBrandingLogo: true,
  shouldShowRightBrandingLogo: false,
  shouldAddSecondPhoneNumber: false,
  residenceOptions: undefined,
  shouldShowMotherMaidenName: false,
  canAbandonApplication: false,
  isProofOfRelationshipBlockedByBasicInfo: false,
  isMultipleMonthlyDeposits: true,
  overridePhoneNumberSchema: {
    Other: { maxLength: 15, pattern: '^\\d{6,15}$' },
  },
};

export const CAYMAN_REGION = {
  GRAND_CAYMAN: 'Grand Cayman',
  CAYMAN_BRAC: 'Cayman Brac',
  LITTLE_CAYMAN: 'Little Cayman',
};

export const GRAND_CAYMAN_DISTRICTS = [
  'George Town',
  'West Bay',
  'Bodden Town',
  'North Side',
  'East End',
] as const;

export const CAYMAN_BRAC_DISTRICTS = [
  'Spot Bay',
  'Stake Bay',
  'Creek',
  'West End',
  'Watering Place',
] as const;

export const LITTLE_CAYMAN_DISTRICTS = ['Little Cayman'] as const;

export const CAYMAN_REGIONS_OPTIONS: Option[] = Object.values(CAYMAN_REGION)
  .sort(sortStringArray)
  .map(region => ({
    text: region,
    value: region,
  }));

export const mapCaymanDistrictRegion: Record<string, readonly string[]> = {
  [CAYMAN_REGION.GRAND_CAYMAN]: GRAND_CAYMAN_DISTRICTS,
  [CAYMAN_REGION.CAYMAN_BRAC]: CAYMAN_BRAC_DISTRICTS,
  [CAYMAN_REGION.LITTLE_CAYMAN]: LITTLE_CAYMAN_DISTRICTS,
};

export const getExtraBasicInfoQuestions = (
  user: User,
): ExtraBasicInfoQuestions => {
  return {
    [QuestionCode.RenouncedUSCitizenship]: {
      enabled:
        user.countryOfBirth === CountryISO.US &&
        user.citizenship !== CountryISO.US &&
        !user.tin?.[CountryISO.US] &&
        hasNonUSIssuedPassport(user) &&
        user.usCitizenshipRenouncement === undefined,
      question: `questionDrawer.RenouncementUS.question`,
      priority: 1,
    },
    [QuestionCode.DualCitizenship]: {
      enabled: user.citizenship2Has === undefined,
      question: `question.${QuestionCode.DualCitizenship}`,
      priority: 2,
    },
    [QuestionCode.TotalAnnualDeposit]: {
      enabled: user.totalAnnualDeposit === undefined,
      question: 'questionDrawer.totalAnnualDeposit.question',
      description: 'questionDrawer.totalAnnualDeposit.description',
      tooltip: 'questionDrawer.totalAnnualDeposit.tooltip',
      priority: 3,
    },
    [QuestionCode.ExpectedMonthlyDeposit]: {
      enabled: !isExpectedMonthlyDepositCollected(user),
      question: 'questionDrawer.expectedMonthlyDeposit',
      priority: 4,
    },
    [QuestionCode.MultipleFundingSources]: {
      enabled: !user?.incomeSources?.length,
      question: 'questionDrawer.sourceOfFunding',
      priority: 5,
    },
    [QuestionCode.AccountTypeOrder]: {
      enabled:
        !!user.accountTypes?.length &&
        !user.accountTypes.some(type => type.primaryAccount),
      question: 'questionDrawer.AccountTypeOrder.question',
      description: 'questionDrawer.AccountTypeOrder.description',
      priority: 6,
    },
  };
};
